import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { LoginPage } from '@/pages/auth/LoginPage'
import { ResetPage } from '@/pages/auth/ResetPage'
import { AppLayout } from '@/pages/app/AppLayout'
import { AdminAccountPage } from '@/pages/app/AdminAccountPage'
import { AdminAccountsPage } from '@/pages/app/AdminAccountsPage'
import { AppealPage } from '@/pages/app/AppealPage'
import { AdminOpsPage } from '@/pages/app/AdminOpsPage'
import { AdminOverviewPage } from '@/pages/app/AdminOverviewPage'
import { HomePage } from '@/pages/home/HomePage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { HistoryPage } from '@/pages/app/HistoryPage'
import { InvoicesPage } from '@/pages/app/InvoicesPage'
import { OverviewPage } from '@/pages/app/OverviewPage'
import { WithdrawalsPage } from '@/pages/app/WithdrawalsPage'
import { PaymentPage } from '@/pages/payment/PaymentPage'
import { Toasts } from '@/shared/ui/Toasts'
import { useAuth } from '@/stores/RootStore'

/**
 * Маршруты приложения.
 *
 * Публичное и приватное разделены явно и в одном месте: страницу
 * оплаты открывает покупатель без входа, кабинет — только с живой
 * сессией. Экраны о проверке не знают и знать не должны, иначе
 * однажды появится экран, который её забудет.
 */
const App = observer(function App() {
  const auth = useAuth()

  useEffect(() => {
    void auth.restore()
  }, [auth])

  return (
    <BrowserRouter>
      <Routes>
        {/* Публичное */}
        <Route path="/" element={<HomePage />} />
        <Route path="/pay/:invoiceId" element={<PaymentPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset" element={<ResetPage />} />

        {/* Кабинет. Проверка сессии стоит на всей ветке разом. */}
        <Route path="/app" element={<Protected><AppLayout /></Protected>}>
          <Route index element={<OverviewPage />} />
          <Route path="history" element={<HistoryPage />} />
          <Route path="withdrawals" element={<WithdrawalsPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="appeal" element={<AppealPage />} />
          <Route path="admin" element={<AdminOnly><AdminOverviewPage /></AdminOnly>} />
          <Route path="admin/accounts" element={<AdminOnly><AdminAccountsPage /></AdminOnly>} />
          <Route path="admin/ops" element={<AdminOnly><AdminOpsPage /></AdminOnly>} />
          <Route path="admin/accounts/:accountId" element={<AdminOnly><AdminAccountPage /></AdminOnly>} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Toasts />
    </BrowserRouter>
  )
})

/**
 * Защищённая часть.
 *
 * Пока идёт восстановление сессии, показываем ожидание, а НЕ экран
 * входа: иначе при каждой перезагрузке страницы человек видит форму
 * входа на долю секунды и успевает решить, что его выкинуло.
 */
const Protected = observer(function Protected({ children }: { children: React.ReactNode }) {
  const auth = useAuth()
  const location = useLocation()

  if (auth.restoring) return <Waiting />
  if (!auth.authenticated) {
    // Запоминаем и query: на истории там стоят фильтры, и вернуть
    // человека на голый список — значит заставить выставить их заново.
    const from = location.pathname + location.search
    return <Navigate to="/login" replace state={{ from }} />
  }
  return <>{children}</>
})

/**
 * Админская часть.
 *
 * Проверка роли на фронте — это удобство, а не защита: настоящую
 * стену держит бекенд на каждой ручке. Здесь она нужна, чтобы
 * мерчант, набравший /app/admin руками, увидел понятный отказ,
 * а не пустой экран с ошибками в консоли.
 */
const AdminOnly = observer(function AdminOnly({ children }: { children: React.ReactNode }) {
  const auth = useAuth()
  if (!auth.isAdmin) return <Navigate to="/app" replace />
  return <>{children}</>
})

function Waiting() {
  return (
    <div style={{
      minHeight: '100dvh', display: 'grid', placeItems: 'center',
      color: 'var(--ink-soft)',
    }}>
      Загружаем кабинет…
    </div>
  )
}

export default App
